import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Check, PenLine, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, size = 16 }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} className={i < Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
    ))}
  </div>
);

const ReviewCard = ({ r }) => (
  <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md border border-gray-100">
    <div className="flex items-center gap-1 mb-2">
      <Stars value={r.rating} />
    </div>
    {r.title && <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight">{r.title}</h3>}
    <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 whitespace-pre-line">{r.text}</p>
    <div className="pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
      <div>
        <p className="text-sm font-semibold text-gray-900">{r.name}</p>
        <p className="text-xs text-gray-500">
          {r.date || (r.createdAt ? new Date(r.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '')}
          {r.color ? ` \u00B7 ${r.color}` : ''}
        </p>
      </div>
      {r.verified && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
          <Check size={12} /> Verified Purchase
        </span>
      )}
    </div>
  </div>
);

export const ProductReviews = ({ product, onStats }) => {
  const [siteReviews, setSiteReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ orderNumber: '', email: '', name: '', rating: 0, title: '', text: '' });
  const [hoverStar, setHoverStar] = useState(0);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/products/${product.id}/reviews`)
      .then((r) => alive && setSiteReviews(r.data.reviews || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [product.id]);

  useEffect(() => {
    if (onStats) onStats({ count: siteReviews.length, sum: siteReviews.reduce((t, r) => t + r.rating, 0) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteReviews]);

  const amazonReviews = product.reviewsList || [];
  const amazonCount = Number(product.reviews) || 0;
  const siteSum = siteReviews.reduce((t, r) => t + r.rating, 0);
  const totalCount = amazonCount + siteReviews.length;
  const combinedAvg = totalCount > 0
    ? Math.round((((Number(product.rating) || 0) * amazonCount + siteSum) / totalCount) * 10) / 10
    : null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        productId: product.id,
        orderNumber: form.orderNumber,
        email: form.email,
        name: form.name,
        rating: form.rating,
        title: form.title || null,
        text: form.text,
      });
      setSiteReviews((rs) => [res.data, ...rs]);
      setShowForm(false);
      setForm({ orderNumber: '', email: '', name: '', rating: 0, title: '', text: '' });
      toast.success('Thanks for your review!', { description: 'Your verified review is now live.' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not submit review', {
        description: typeof detail === 'string' ? detail : 'Please check your order number and email.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-14 sm:mb-20" data-testid="product-reviews-section">
      <div className="mb-6 sm:mb-8">
        <span className="text-xs sm:text-sm font-semibold text-amber-600 bg-amber-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full uppercase tracking-wider">
          Customer Reviews
        </span>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-3 sm:mt-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">What Our Customers Are Saying</h2>
            {combinedAvg !== null && (
              <div className="flex items-center gap-2 sm:gap-3 mt-3">
                <Stars value={combinedAvg} size={18} />
                <span className="text-sm sm:text-base text-gray-600 font-medium" data-testid="reviews-average">
                  {combinedAvg} out of 5 &middot; {totalCount} review{totalCount === 1 ? '' : 's'}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="write-review-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors duration-300"
          >
            <PenLine size={16} />
            {showForm ? 'Close' : 'Write a Review'}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg border border-gray-100 mb-8" data-testid="review-form">
          <p className="text-sm text-gray-600 mb-5">
            Reviews are for verified buyers. Enter the order number and email from your order confirmation —
            reviews open once your item has had time to arrive.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Order Number *</label>
              <input
                required
                value={form.orderNumber}
                onChange={set('orderNumber')}
                placeholder="e.g. 4F2A91C3"
                data-testid="review-order-number"
                className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Order Email *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="you@example.com"
                data-testid="review-email"
                className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Display Name *</label>
              <input
                required
                value={form.name}
                onChange={set('name')}
                placeholder="How your name appears"
                data-testid="review-name"
                className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Your Rating *</label>
              <div className="flex items-center gap-1 h-11" data-testid="review-star-picker">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    onMouseEnter={() => setHoverStar(n)}
                    onMouseLeave={() => setHoverStar(0)}
                    data-testid={`review-star-${n}`}
                    className="p-0.5"
                  >
                    <Star
                      size={26}
                      className={n <= (hoverStar || form.rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">Review Title</label>
            <input
              value={form.title}
              onChange={set('title')}
              placeholder="Sum it up in a few words (optional)"
              data-testid="review-title"
              className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">Your Review *</label>
            <textarea
              required
              rows={4}
              value={form.text}
              onChange={set('text')}
              placeholder="How is the product working out for you?"
              data-testid="review-text"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm resize-y"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="review-submit"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:shadow-xl transition-all duration-300 disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Submit Review
          </button>
        </form>
      )}

      {(siteReviews.length > 0 || amazonReviews.length > 0) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-testid="reviews-list">
          {siteReviews.map((r) => (
            <ReviewCard key={r.id} r={r} />
          ))}
          {amazonReviews.map((r, idx) => (
            <ReviewCard key={`amz-${idx}`} r={r} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500" data-testid="no-reviews-note">
          No reviews yet — be the first verified buyer to share your experience.
        </p>
      )}
    </div>
  );
};

export default ProductReviews;
